import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

const skills = [
  {
    title: "Frontend Development",
    items: [
      "React (TypeScript)",
      "Material UI & component-based design",
      "Responsive layouts & PWA patterns",
      "State management and data-driven UI",
      "UX-focused interface design",
    ],
  },
  {
    title: "Backend Development",
    items: [
      "C# (.NET / ASP.NET Core)",
      "REST & GraphQL APIs",
      "Entity Framework & data access layers",
    ],
  },
  {
    title: "Data & Integration",
    items: ["SQL Server", "MongoDB", "API integration and data modelling"],
  },
  {
    title: "Tools & Practices",
    items: [
      "Git (Azure DevOps)",
      "Agile (Scrum)",
      "CI/CD workflows",
      "Debugging and performance analysis",
      "Figma (UI/UX design & prototyping)",
    ],
  },
];

const experience = [
  {
    role: "Software Developer",
    period: "2019 – Present",
    points: [
      "Design, develop, and maintain full-stack applications using React, GraphQL, and .NET",
      "Build responsive, user-focused interfaces with an emphasis on usability and clarity",
      "Develop and maintain backend services and APIs to support application features and data flow",
      "Work with relational and non-relational databases to manage and optimise data storage",
      "Diagnose and resolve complex issues across frontend, backend, and API layers",
      "Collaborate in agile teams, contributing to sprint planning, code reviews, and continuous improvement",
      "Participate in UI/UX design and prototyping to improve user experience",
    ],
  },
  {
    role: "Software Engineer Intern",
    period: "2018",
    points: [
      "Supported development of frontend and backend components",
      "Gained experience in full-stack workflows and database integration",
    ],
  },
];

const SkillCard = ({
  title,
  items,
}: {
  title: string;
  items: string[];
}) => (
  <Card elevation={3} sx={{ height: "100%" }}>
    <CardContent>
      <Typography variant="h6" fontWeight={400} color="primary" gutterBottom sx={{ fontFamily: "inherit" }}>
        {title}
      </Typography>
      <List dense disablePadding>
        {items.map((item) => (
          <ListItem key={item} disableGutters sx={{ py: 0.25 }}>
            <ListItemText
              primary={item}
              slotProps={{ primary: { variant: "body2", color: "text.primary" } }}
            />
          </ListItem>
        ))}
      </List>
    </CardContent>
  </Card>
);

const FeaturesSection = () => (
  <Box sx={{ bgcolor: "background.default" }}>
    {/* Core Skills */}
    <Container sx={{ py: 8 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom textAlign="center">
        Core Skills
      </Typography>
      <Divider sx={{ mb: 4 }} />
      <Grid container spacing={3}>
        {skills.map((skill) => (
          <Grid key={skill.title} size={{ xs: 12, sm: 6, md: 3 }}>
            <SkillCard title={skill.title} items={skill.items} />
          </Grid>
        ))}
      </Grid>
    </Container>

    {/* Experience */}
    <Box sx={{ bgcolor: "background.paper", py: 8 }}>
      <Container>
        <Typography variant="h4" fontWeight={700} gutterBottom textAlign="center">
          Professional Experience
        </Typography>
        <Divider sx={{ mb: 4 }} />
        <Grid container justifyContent="center">
          <Grid size={{ xs: 12, md: 8 }}>
            <Card elevation={3}>
              <CardContent>
                {/* Main role */}
                <Typography variant="h6" fontWeight={400} color="primary" sx={{ fontFamily: "inherit" }}>
                  Software Developer
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Ireland | 2019 – Present
                </Typography>
                <List dense disablePadding>
                  {experience[0].points.map((point) => (
                    <ListItem key={point} disableGutters sx={{ py: 0.25, alignItems: "flex-start" }}>
                      <ListItemText
                        primary={`• ${point}`}
                        slotProps={{ primary: { variant: "body2", color: "text.primary" } }}
                      />
                    </ListItem>
                  ))}
                </List>

                {/* Intern subsection */}
                <Box sx={{ mt: 3, pl: 2, borderLeft: "2px solid", borderColor: "divider" }}>
                  <Typography variant="body2" fontWeight={400} color="primary" sx={{ fontFamily: "inherit" }}>
                    Software Engineer Intern · 2018
                  </Typography>
                  <List dense disablePadding sx={{ mt: 0.5 }}>
                    {experience[1].points.map((point) => (
                      <ListItem key={point} disableGutters sx={{ py: 0.25, alignItems: "flex-start" }}>
                        <ListItemText
                          primary={`• ${point}`}
                          slotProps={{ primary: { variant: "body2", color: "text.primary" } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>

    {/* Education */}
    <Container sx={{ py: 8 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom textAlign="center">
        Education
      </Typography>
      <Divider sx={{ mb: 4 }} />
      <Grid container spacing={3} justifyContent="center">
        {[
          "BSc (Hons) Software Development",
          "Professional Diploma in UX Design",
        ].map((qual) => (
          <Grid key={qual} size={{ xs: 12, sm: 6, md: 4 }}>
            <Card elevation={3}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="body1" fontWeight={500}>
                  {qual}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

export default FeaturesSection;
